import React, { useState } from 'react'
import { Button, message } from 'antd'
import { notifyBranchesChanged } from '../../../../../BranchSwitcher/events'
import { createOrganization } from '../../api/organization'
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'
import type { CreateOrgPayload, Organization } from '../../types'
import { makeOrgShort, pickOrgColor } from '../../utils'
import CreateOrgModal from './CreateOrgModal'

export interface NewOrganizationResult {
  organization: Organization
  token: string
}

interface NewOrganizationProps {
  orgCount: number
  canCreate: boolean
  sourcePlan?: string
  onCreated: (result: NewOrganizationResult) => void
}

const NewOrganization: React.FC<NewOrganizationProps> = ({ orgCount, canCreate, sourcePlan, onCreated }) => {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  if (!canCreate) {
    return null
  }

  const handleSubmit = async (payload: CreateOrgPayload) => {
    setSubmitting(true)
    try {
      const result = await createOrganization(payload)
      notifyBranchesChanged({
        customerId: result.customerId,
        companyName: result.companyName,
      })

      const organization: Organization = {
        id: result.customerId,
        customerId: result.customerId,
        name: result.companyName,
        short: makeOrgShort(result.companyName),
        color: pickOrgColor(orgCount),
        address: [payload.street, payload.city, payload.state, payload.zip].filter(Boolean).join(', '),
        plan: result.plan,
        memberCount: 1,
        myRole: 'Super Admin',
        roles: ['LoanOfficer', 'LoanOfficerManager', 'Owner', 'SuperAdmin'],
        canManageUsers: true,
        canCreateOrganization: true,
      }

      onCreated({ organization, token: result.token })
      setOpen(false)
      message.success('Organization created — you are its admin')
      track(STATSIG.USER_MANAGEMENT.ORG_INSERT, payload.companyName, { organization_name: payload.companyName })
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to create organization')
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button className="um-refactor__new-org-btn" onClick={() => {
        track(STATSIG.USER_MANAGEMENT.NEW_ORG_BUTTON, 'click_open_popup', { type: 'click_open_popup' })
        setOpen(true)
      }}>
        ＋ New organization
      </Button>
      <CreateOrgModal
        open={open}
        submitting={submitting}
        sourcePlan={sourcePlan}
        onCancel={() => setOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  )
}

export default NewOrganization
